import React, { useEffect, useState } from 'react';
import { Typography, Row, Col } from 'antd';
import AuthTest from '../components/auth/AuthTest';
import Products from './Products';
import {
  DEFAULT_HOMEPAGE_CONTENT,
  HomepageContent,
  heroImageUrl,
  homepageService,
  heroPhotoLayerStyle,
  photoWebUrl,
} from '../services/homepageService';

const DEFAULT_HERO_BACKGROUND =
  'linear-gradient(135deg, var(--dasoon-green) 0%, var(--dasoon-bg-dark) 70%)';

/**
 * The hero's background (issue #140): the colour staff picked, otherwise the
 * built-in gradient. An uploaded photo is drawn over it in its own layer
 * (issue #162), so the colour shows behind the photo while it loads.
 */
const heroBackground = (color: string, image: string): string => {
  if (image) return color || 'var(--dasoon-bg-dark)';
  return color || DEFAULT_HERO_BACKGROUND;
};

const { Title, Paragraph } = Typography;

const Home: React.FC = () => {
  // Hero copy, hero image and the promo strip are admin-editable (issue #132).
  // Starting from the shipped defaults means the page never renders an empty
  // hero while the request is in flight, and survives it failing outright.
  const [content, setContent] = useState<HomepageContent>(DEFAULT_HOMEPAGE_CONTENT);

  useEffect(() => {
    let active = true;
    homepageService
      .getContent()
      .then(fetched => {
        if (active) setContent(fetched);
      })
      .catch(error => {
        console.error('Failed to load homepage content:', error);
      });
    return () => {
      active = false;
    };
  }, []);

  const usesBuiltInHero = !content.heroBgColor && !content.heroBgImage;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="dasoon-hero-section" style={{
        background: heroBackground(content.heroBgColor, content.heroBgImage),
        minHeight: '60vh',
        padding: '48px 0',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* The glow belongs to the built-in gradient; on a staff colour or
            photo it would tint their choice. */}
        {usesBuiltInHero && <div className="hero-pattern" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: `
            radial-gradient(circle at 20% 20%, rgba(255, 174, 0, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 174, 0, 0.05) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>}

        {/* The photo, darkened so the white headline reads on it, framed on
            the focus point and zoom staff chose (issues #154, #162). */}
        {content.heroBgImage && (
          <div
            aria-hidden
            style={heroPhotoLayerStyle(photoWebUrl(content.heroBgImage), content.heroBgPosition, content.heroBgZoom)}
          />
        )}

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <Row gutter={[48, 32]} align="middle">
            <Col xs={24} lg={12}>
              <div style={{ textAlign: 'left' }}>
                <Title level={1} style={{
                  color: 'var(--dasoon-white)',
                  fontSize: 'clamp(2rem, 7vw, 3.5rem)',
                  fontFamily: 'var(--font-headers)',
                  fontWeight: 700,
                  marginBottom: 'var(--spacing-lg)',
                  lineHeight: 1.1,
                  letterSpacing: 'var(--font-headers-spacing)'
                }}>
                  {content.heroHeadline}
                </Title>
                <Paragraph style={{ 
                  fontSize: 'var(--text-xl)', 
                  color: 'var(--dasoon-white)',
                  marginBottom: 'var(--spacing-2xl)', 
                  lineHeight: 1.6,
                  fontFamily: 'var(--font-main)',
                  opacity: 0.9
                }}>
                  {content.heroSubtext}
                </Paragraph>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div style={{ textAlign: 'center', position: 'relative' }}>
                <img 
                  src={heroImageUrl(content.heroImage)} 
                  alt="Quality Eggs" 
                  onError={(e) => {
                    // An uploaded banner that has gone missing shouldn't leave
                    // a broken image in the hero.
                    (e.target as HTMLImageElement).src = '/images/dasoon-hero-egg.png';
                  }}
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    filter: 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3))',
                    animation: 'float 6s ease-in-out infinite'
                  }}
                />
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Promotional banner — off unless staff have switched it on with
          something to say (issue #132). */}
      {content.promoEnabled && content.promoText && (
        <section
          style={{
            background: 'var(--dasoon-yellow, #FFAE00)',
            color: 'var(--dasoon-bg-dark, #1a1a1a)',
            padding: '14px 16px',
            textAlign: 'center',
            fontFamily: 'var(--font-main)',
            fontWeight: 600,
            fontSize: 'var(--text-lg)',
          }}
        >
          <div className="container" style={{ whiteSpace: 'pre-wrap' }}>
            {content.promoText}
          </div>
        </section>
      )}

      {/* The catalogue sits directly under the hero: Home and Products are one
          page (issue #189). */}
      <Products />

      {/* Auth Test Section - Development Only */}
      {import.meta.env.DEV && (
        <div className="py-8 bg-yellow-50 border-t-2 border-yellow-200">
          <div className="container mx-auto px-4">
            <div className="text-center mb-4">
              <span className="text-yellow-600 font-semibold text-sm">
                DEVELOPMENT MODE - Auth Testing
              </span>
            </div>
            <AuthTest />
          </div>
        </div>
      )}

    </div>
  );
};

export default Home;